import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../AuthContext';
import { api } from '../services/api';
import { Search, Calculator, TrendingUp, Package, Clock, ArrowUpDown, ChevronUp, ChevronDown, Download } from 'lucide-react';
import { io } from 'socket.io-client';
import { useSortableData } from '../hooks/useSortableData';
import Pagination from './Pagination';
const FulfillmentForecast = () => {
    const { user } = useAuth();
    const [forecasts, setForecasts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [clientSearch, setClientSearch] = useState('All');
    const [warehouseFilter, setWarehouseFilter] = useState('All');
    const [statusFilter, setStatusFilter] = useState('All'); // Default to All status
    const [skuSearch, setSkuSearch] = useState('');
    const [searchBySKU, setSearchBySKU] = useState(true);
    const [searchByProduct, setSearchByProduct] = useState(true);
    const [forecastDays, setForecastDays] = useState(7);

    // Helper for consistent health status derivation
    const getRunwayHealthStatus = (days) => {
        const d = Number(days);
        if (d > 30) return 'Healthy';
        if (d > 14) return 'Reorder Soon';
        return 'Critical Risk';
    };
    const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 25;

    useEffect(() => {
        const fetchForecasts = async () => {
            try {
                let params = { days: forecastDays };
                if (forecastDays === 'custom') {
                    if (customDateRange.start && customDateRange.end) {
                        params = { startDate: customDateRange.start, endDate: customDateRange.end };
                    } else {
                        // Wait for the user to select both dates
                        return;
                    }
                }
                const res = await api.inventory.getForecast(params).catch(() => null);
                if (res?.data && Array.isArray(res.data)) {
                    setForecasts(res.data);
                } else {
                    setForecasts([]);
                }
            } catch (error) {
                console.error("Forecast error", error);
            } finally {
                setLoading(false);
            }
        };

        fetchForecasts();

        const socketUrl = (process.env.REACT_APP_API_URL || 'http://localhost:8009/api').replace('/api', '');
        const socket = io(socketUrl);

        socket.on('forecast_updated', (data) => {
            console.log("WebSocket: Forecast Updated", data);
            fetchForecasts();
        });

        socket.on('inventory_updated', (data) => {
            console.log("WebSocket: Inventory Updated", data);
            fetchForecasts();
        });

        return () => socket.disconnect();
    }, [forecastDays, customDateRange.start, customDateRange.end]);

    const uniqueWarehouses = useMemo(() => {
        return ['All', ...new Set(forecasts.map(f => f.warehouseName).filter(Boolean))];
    }, [forecasts]);

    // Only include client names with alphabetic characters (exclude all-numeric or code-like values)
    const isValidClientName = (name) => {
        if (!name) return false;
        // Exclude if all digits or mostly digits/codes
        return /[a-zA-Z]/.test(name);
    };

    const uniqueClients = useMemo(() => {
        const names = forecasts.map(f => f.clientName).filter(isValidClientName);
        return ['All', ...Array.from(new Set(names))].sort((a, b) => a === 'All' ? -1 : b === 'All' ? 1 : a.localeCompare(b));
    }, [forecasts]);

    // FIXED: Removed sorting logic from filteredForecasts - now only filters, doesn't sort
    const filteredForecasts = useMemo(() => {
        const filtered = forecasts.filter(f => {
            const matchesClient = clientSearch === 'All' || f.clientName === clientSearch;
            const matchesWarehouse = warehouseFilter === 'All' || f.warehouseName === warehouseFilter;
            const matchesSku = !skuSearch || 
                (searchBySKU && f.sku.toLowerCase().startsWith(skuSearch.toLowerCase())) || 
                (searchByProduct && f.productName && f.productName.toLowerCase().startsWith(skuSearch.toLowerCase()));

            const health = getRunwayHealthStatus(f.estimatedRunwayDays);
            const matchesStatus = statusFilter === 'All' || health === statusFilter;

            return matchesClient && matchesWarehouse && matchesStatus && matchesSku;
        });

        // Return filtered array without any sorting - let useSortableData handle all sorting
        return filtered;
    }, [forecasts, clientSearch, warehouseFilter, statusFilter, skuSearch, searchBySKU, searchByProduct]);

    const { items: sortedForecasts, requestSort, sortConfig } = useSortableData(filteredForecasts);

    const totalPages = Math.ceil(sortedForecasts.length / itemsPerPage);
    const paginatedForecasts = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return sortedForecasts.slice(start, start + itemsPerPage);
    }, [sortedForecasts, currentPage]);

    const getSortIcon = (key) => {
        if (!sortConfig || sortConfig.key !== key) return <ArrowUpDown size={14} style={{ display: 'inline', marginLeft: '4px', opacity: 0.3 }} />;
        return sortConfig.direction === 'ascending' ? <ChevronUp size={14} style={{ display: 'inline', marginLeft: '4px', color: 'var(--primary)' }} /> : <ChevronDown size={14} style={{ display: 'inline', marginLeft: '4px', color: 'var(--primary)' }} />;
    };

    const showClientCol = clientSearch === 'All';
    const showWarehouseCol = warehouseFilter === 'All';

    const exportToCSV = () => {
        const periodLabel = forecastDays === 'custom'
            ? `${customDateRange.start}_to_${customDateRange.end}`
            : `${forecastDays}days`;

        const headers = [
            'Client / Account',
            'Warehouse',
            'SKU',
            'Product',
            `Orders (${forecastDays === 'custom' ? 'Custom' : `${forecastDays} Days`})`,
            'Avg Orders/Day',
            'Inventory On Hand',
            'Est. Runway (Days)',
            'Runway Health'
        ];

        const rows = sortedForecasts.map(f => [
            f.clientName ?? '',
            f.warehouseName ?? '',
            f.sku ?? '',
            f.productName ?? '',
            f.totalOrdersPeriod ?? f.totalOrdersLast7Days ?? 0,
            f.averageOrdersPerDay ?? 0,
            f.totalInventoryOnHand ?? 0,
            f.estimatedRunwayDays === 999 ? '999+' : f.estimatedRunwayDays,
            getRunwayHealthStatus(f.estimatedRunwayDays)
        ]);

        const escapeCell = (val) => {
            const str = String(val);
            return str.includes(',') || str.includes('"') || str.includes('\n')
                ? `"${str.replace(/"/g, '""')}"`
                : str;
        };

        const csvContent = [
            headers.map(escapeCell).join(','),
            ...rows.map(row => row.map(escapeCell).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `fulfillment_forecast_${periodLabel}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [clientSearch, warehouseFilter, statusFilter, skuSearch]);

    if (loading) return <div className="p-8">Loading client forecasts...</div>;

    // Aggregate values for summary cards over the filtered list
    const aggregateOrdersPeriod = filteredForecasts.reduce((sum, f) => sum + (f.totalOrdersPeriod || f.totalOrdersLast7Days || 0), 0);
    const aggregateAverageDaily = filteredForecasts.reduce((sum, f) => sum + f.averageOrdersPerDay, 0);
    const aggregateInventory = filteredForecasts.reduce((sum, f) => sum + f.totalInventoryOnHand, 0);

    return (
        <div>
            <div className="page-header" style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                    <h1>Client Fulfillment Forecasts</h1>
                    <p>Calculated warehouse runway and daily order velocity per client distribution point.</p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    {[3, 7, 15, 30].map(days => (
                        <button
                            key={days}
                            onClick={() => { setForecastDays(days); setCustomDateRange({ start: '', end: '' }); }}
                            style={{
                                padding: '0.4rem 0.8rem',
                                borderRadius: '6px',
                                border: '1px solid var(--primary)',
                                background: forecastDays === days ? 'var(--primary)' : 'transparent',
                                color: forecastDays === days ? 'white' : 'var(--primary)',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                transition: 'all 0.2s'
                            }}
                        >
                            {days} Days
                        </button>
                    ))}
                    <button
                        onClick={() => setForecastDays('custom')}
                        style={{
                            padding: '0.4rem 0.8rem',
                            borderRadius: '6px',
                            border: '1px solid var(--primary)',
                            background: forecastDays === 'custom' ? 'var(--primary)' : 'transparent',
                            color: forecastDays === 'custom' ? 'white' : 'var(--primary)',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            transition: 'all 0.2s'
                        }}
                    >
                        Custom
                    </button>

                    {forecastDays === 'custom' && (
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginLeft: '0.5rem' }}>
                            <input
                                type="date"
                                value={customDateRange.start}
                                onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                                style={{
                                    padding: '0.4rem 0.6rem',
                                    borderRadius: '6px',
                                    border: '1px solid #e2e8f0',
                                    outline: 'none',
                                    fontSize: '0.875rem'
                                }}
                            />
                            <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>to</span>
                            <input
                                type="date"
                                value={customDateRange.end}
                                min={customDateRange.start}
                                onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                                style={{
                                    padding: '0.4rem 0.6rem',
                                    borderRadius: '6px',
                                    border: '1px solid #e2e8f0',
                                    outline: 'none',
                                    fontSize: '0.875rem'
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Top Level Metric Summary (Aggregated for current view) */}
            <div className="metrics-grid">
                <div className="card metric-card">
                    <div className="metric-icon">
                        <TrendingUp size={28} />
                    </div>
                    <div className="metric-content">
                        <p>Total Orders ({forecastDays === 'custom' ? 'Custom Date Range' : `${forecastDays} Days`})</p>
                        <h3>{aggregateOrdersPeriod.toLocaleString()}</h3>
                    </div>
                </div>

                <div className="card metric-card">
                    <div className="metric-icon" style={{ backgroundColor: 'rgba(253, 206, 6, 0.15)', color: 'var(--accent)' }}>
                        <Calculator size={28} />
                    </div>
                    <div className="metric-content">
                        <p>Total Average / Day</p>
                        <h3>{aggregateAverageDaily.toLocaleString(undefined, { maximumFractionDigits: 0 })}</h3>
                    </div>
                </div>

                <div className="card metric-card accent-icon">
                    <div className="metric-icon">
                        <Package size={28} />
                    </div>
                    <div className="metric-content">
                        <p>Total Network Inventory</p>
                        <h3>{aggregateInventory.toLocaleString()}</h3>
                    </div>
                </div>
            </div>

            <div className="card" style={{ marginBottom: '1.5rem', padding: '1rem 1.5rem', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <div style={{ flex: '1', minWidth: '200px', position: 'relative' }}>
                    <div style={{ position: 'relative' }}>
                        <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                        <input
                            type="text"
                            placeholder="Search..."
                            value={skuSearch}
                            onChange={(e) => {
                                setSkuSearch(e.target.value);
                            }}
                            style={{
                                width: '100%', padding: '0.6rem 1rem 0.6rem 2.5rem',
                                borderRadius: '6px', border: '1px solid #e2e8f0',
                                outline: 'none', fontFamily: 'inherit'
                            }}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>
                            <input 
                                type="checkbox" 
                                checked={searchBySKU} 
                                onChange={(e) => setSearchBySKU(e.target.checked)} 
                            /> SKU
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>
                            <input 
                                type="checkbox" 
                                checked={searchByProduct} 
                                onChange={(e) => setSearchByProduct(e.target.checked)} 
                            /> Product
                        </label>
                    </div>
                </div>
                <div style={{ flex: '1', minWidth: '180px' }}>
                    <select
                        value={clientSearch}
                        onChange={(e) => setClientSearch(e.target.value)}
                        style={{
                            width: '100%', padding: '0.6rem 1rem',
                            borderRadius: '6px', border: '1px solid #e2e8f0',
                            outline: 'none', fontFamily: 'inherit',
                            backgroundColor: 'white', color: 'var(--text-main)'
                        }}
                    >
                        {uniqueClients.map(client => (
                            <option key={client} value={client}>{client === 'All' ? 'All Clients/Accounts' : client}</option>
                        ))}
                    </select>
                </div>

                {user?.role !== 'manager' && (
                    <div style={{ flex: '1', minWidth: '200px' }}>
                        <select
                            value={warehouseFilter}
                            onChange={(e) => setWarehouseFilter(e.target.value)}
                            style={{
                                width: '100%', padding: '0.6rem 1rem',
                                borderRadius: '6px', border: '1px solid #e2e8f0',
                                outline: 'none', fontFamily: 'inherit',
                                backgroundColor: 'white', color: 'var(--text-main)'
                            }}
                        >
                            {uniqueWarehouses.map(wh => (
                                <option key={wh} value={wh}>{wh === 'All' ? 'All Warehouses' : wh}</option>
                            ))}
                        </select>
                    </div>
                )}

                <div style={{ flex: '1', minWidth: '200px' }}>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        style={{
                            width: '100%', padding: '0.6rem 1rem',
                            borderRadius: '6px', border: '1px solid #e2e8f0',
                            outline: 'none', fontFamily: 'inherit',
                            backgroundColor: 'white', color: 'var(--text-main)'
                        }}
                    >
                        <option value="All">All Health Statuses</option>
                        <option value="Healthy">Healthy</option>
                        <option value="Reorder Soon">Reorder Soon</option>
                        <option value="Critical Risk">Critical Risk</option>
                    </select>
                </div>

                <button
                    onClick={exportToCSV}
                    disabled={sortedForecasts.length === 0}
                    title="Export visible data as CSV"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.4rem',
                        padding: '0.6rem 1.1rem',
                        borderRadius: '6px',
                        border: '1px solid var(--primary)',
                        background: sortedForecasts.length === 0 ? '#f8fafc' : 'var(--primary)',
                        color: sortedForecasts.length === 0 ? '#94a3b8' : 'white',
                        cursor: sortedForecasts.length === 0 ? 'not-allowed' : 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s'
                    }}
                >
                    <Download size={16} />
                    Export CSV
                </button>
            </div>

            <div className="card table-container">
                <table className="responsive-table">
                    <thead>
                        <tr>
                            {showClientCol && <th className="sortable" onClick={() => requestSort('clientName')}>Client / Account {getSortIcon('clientName')}</th>}
                            {showWarehouseCol && <th className="sortable" onClick={() => requestSort('warehouseName')}>Warehouse {getSortIcon('warehouseName')}</th>}
                            <th className="sortable" onClick={() => requestSort('sku')}>SKU {getSortIcon('sku')}</th>
                            <th className="sortable" onClick={() => requestSort('productName')}>Product {getSortIcon('productName')}</th>
                            <th className="sortable" style={{ textAlign: 'right' }} onClick={() => requestSort('totalOrdersPeriod')}>Orders ({forecastDays === 'custom' ? 'Custom' : `${forecastDays} Days`}) {getSortIcon('totalOrdersPeriod')}</th>
                            <th className="sortable" style={{ textAlign: 'right' }} onClick={() => requestSort('averageOrdersPerDay')}>Avg Orders/Day {getSortIcon('averageOrdersPerDay')}</th>
                            <th className="sortable" style={{ textAlign: 'right' }} onClick={() => requestSort('totalInventoryOnHand')}>Inventory On Hand {getSortIcon('totalInventoryOnHand')}</th>
                            <th className="sortable" style={{ textAlign: 'right' }} onClick={() => requestSort('estimatedRunwayDays')}>Est. Runway {getSortIcon('estimatedRunwayDays')}</th>
                            <th className="sortable" style={{ textAlign: 'center' }} onClick={() => requestSort('estimatedRunwayDays')}>Runway Health {getSortIcon('estimatedRunwayDays')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedForecasts.length === 0 ? (
                            <tr>
                                <td colSpan={7 + (showClientCol ? 1 : 0) + (showWarehouseCol ? 1 : 0)} style={{ textAlign: 'center', padding: '4rem 2rem', color: 'var(--text-muted)' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
                                        <Package size={48} style={{ opacity: 0.2 }} />
                                        <div>
                                            <p style={{ fontWeight: 500, color: 'var(--text-main)', marginBottom: '0.25rem' }}>No inventory forecast data found</p>
                                            <p style={{ fontSize: '0.875rem' }}>Looks like there aren't any records matching your criteria. Sync ShipHero data or adjust your filters.</p>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            paginatedForecasts.map((forecast) => {
                                const healthStatus = getRunwayHealthStatus(forecast.estimatedRunwayDays);
                                const runwayColor = healthStatus === 'Healthy'
                                    ? 'var(--success)' : healthStatus === 'Reorder Soon'
                                        ? 'var(--warning)' : 'var(--danger)';

                                return (
                                    <tr key={`${forecast.sku}-${forecast.warehouseName}-${forecast.clientName}`}>
                                        {showClientCol && <td data-label="Client / Account" style={{ fontWeight: 600, color: 'var(--primary)' }} title={forecast.clientName}>{forecast.clientName}</td>}
                                        {showWarehouseCol && <td data-label="Warehouse" style={{ color: 'var(--text-main)' }} title={forecast.warehouseName}>{forecast.warehouseName}</td>}
                                        <td data-label="SKU" style={{ fontFamily: 'monospace', fontSize: '0.875rem' }} title={forecast.sku}>{forecast.sku}</td>
                                        <td data-label="Product" style={{ color: 'var(--text-main)', fontSize: '0.875rem' }} title={forecast.productName}>{forecast.productName}</td>
                                        <td data-label={`Orders (${forecastDays === 'custom' ? 'Custom' : `${forecastDays} Days`})`} style={{ textAlign: 'right' }}>{(forecast.totalOrdersPeriod || forecast.totalOrdersLast7Days || 0).toLocaleString()}</td>
                                        <td data-label="Avg Orders/Day" style={{ textAlign: 'right' }}>{forecast.averageOrdersPerDay}</td>
                                        <td data-label="Inventory On Hand" style={{ textAlign: 'right' }}>{forecast.totalInventoryOnHand.toLocaleString()}</td>
                                        <td data-label="Est. Runway" style={{ textAlign: 'right', fontWeight: 'bold', color: runwayColor }}>
                                            {forecast.estimatedRunwayDays === 999 ? '999+' : forecast.estimatedRunwayDays} days
                                        </td>
                                        <td data-label="Runway Health" style={{ textAlign: 'center' }}>
                                            {healthStatus === 'Healthy' ? (
                                                <span className="badge success">Healthy</span>
                                            ) : healthStatus === 'Reorder Soon' ? (
                                                <span className="badge warning" style={{ backgroundColor: 'rgba(253, 206, 6, 0.15)', color: '#d97706', border: '1px solid rgba(253, 206, 6, 0.3)' }}>Reorder Soon</span>
                                            ) : (
                                                <span className="badge danger" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', color: 'var(--danger)', border: '1px solid rgba(239, 68, 68, 0.2)' }}>Critical Risk</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>

                {sortedForecasts.length > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', backgroundColor: 'white', borderTop: '1px solid #e2e8f0', borderRadius: '0 0 8px 8px' }}>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, sortedForecasts.length)} of {sortedForecasts.length} entries
                        </div>
                            <Pagination currentPage={currentPage} totalPages={totalPages} setCurrentPage={setCurrentPage} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default FulfillmentForecast;